import _ from 'lodash';
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import AttachmentModal from 'components/attachments/attachment_modal';
import AttachmentPreviewOpener from 'components/lib/attachment/attachment_preview_opener';
import AttachmentStatus from 'models/attachment/attachment_status';
import CustomChannelMessageAttachment from 'models/custom_channel_message_attachment';
import GenericAttachment from 'components/lib/attachment/generic_attachment';
import { getCustomChannelAttachmentUrl } from 'scripts/domain/services/attachments';
import { HandleHeightChange } from 'components/customer/conversation_history/virtualized/list_item';
import InlineImageAttachment from 'components/lib/attachment/inline_image_attachment';
import InlineVideoAttachment from 'components/lib/attachment/inline_video_attachment';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';

const CustomChannelMessageAttachmentsContainer = AttachmentPreviewOpener(CustomChannelMessageAttachments);

function CustomChannelMessageAttachments({ attachments, itemId, orgId }) {
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const isFeatureEnabled = useIsFeatureEnabled();
  if (_.isEmpty(attachments)) {
    return null;
  }

  const videoPreviewEnabled = isFeatureEnabled('videoAttachmentPreview');
  const [inlineAttachments, genericAttachments] = _.partition(
    attachments,
    a =>
      !a.isRedacted && (a.isImage() || (videoPreviewEnabled && a.isVideo())) && a.status === AttachmentStatus.AVAILABLE
  );
  const availableAttachments = _.filter(attachments, a => a.status === AttachmentStatus.AVAILABLE);

  return (
    <React.Fragment>
      <div className="itemAttachments">
        <ul className="attachments-inlineImageAttachments">{_.map(inlineAttachments, renderInlineAttachment)}</ul>
        <ul className="genericAttachments">{_.map(genericAttachments, renderGenericAttachment)}</ul>
      </div>

      {previewAttachment && (
        <AttachmentModal
          attachment={previewAttachment}
          attachments={availableAttachments}
          setAttachment={setPreviewAttachment}
          url={getDownloadUrl(previewAttachment)}
        />
      )}
    </React.Fragment>
  );

  function renderGenericAttachment(attachment, index) {
    return (
      <GenericAttachment
        attachment={attachment}
        key={attachment.id}
        onClick={(attachment.status === AttachmentStatus.AVAILABLE && openAttachmentPreview) || undefined}
      />
    );

    function openAttachmentPreview() {
      setPreviewAttachment(attachment);
    }
  }

  function renderInlineAttachment(attachment, index) {
    return attachment.isVideo()
      ? renderInlineVideoAttachment(attachment, index)
      : renderInlineImageAttachment(attachment, index);
  }

  function renderInlineImageAttachment(attachment, index) {
    return (
      <HandleHeightChange key={attachment.id}>
        {({ onHeightChange }) => (
          <InlineImageAttachment
            attachment={attachment}
            inlinePreviewImageUrl={getPreviewUrl()}
            key={attachment.id}
            onClick={openAttachmentPreview}
            onLoad={onHeightChange}
          />
        )}
      </HandleHeightChange>
    );

    function getPreviewUrl() {
      let imageUrl = getDownloadUrl(attachment);
      return `${imageUrl}?size=preview`;
    }

    function openAttachmentPreview() {
      setPreviewAttachment(attachment);
    }
  }

  function renderInlineVideoAttachment(attachment, index) {
    return (
      <HandleHeightChange key={attachment.id}>
        {({ onHeightChange }) => (
          <InlineVideoAttachment
            attachment={attachment}
            inlinePreviewVideoUrl={getDownloadUrl(attachment)}
            key={attachment.id}
            onClick={openAttachmentPreview}
            onLoad={onHeightChange}
          />
        )}
      </HandleHeightChange>
    );

    function openAttachmentPreview() {
      setPreviewAttachment(attachment);
    }
  }

  function getDownloadUrl(a) {
    return getCustomChannelAttachmentUrl(orgId, a);
  }
}

CustomChannelMessageAttachmentsContainer.propTypes = {
  attachments: PropTypes.arrayOf(PropTypes.instanceOf(CustomChannelMessageAttachment)).isRequired,
  customChannelId: PropTypes.string.isRequired,
  itemId: PropTypes.string.isRequired,
  onHeightChange: PropTypes.func,
};

CustomChannelMessageAttachments.propTypes = {
  ...AttachmentPreviewOpener.childPropTypes,
  ...CustomChannelMessageAttachmentsContainer.propTypes,
};

export { CustomChannelMessageAttachments };
export default CustomChannelMessageAttachmentsContainer;
